"use client"

import { type RefObject, useCallback, useEffect, useRef } from "react"

type ManagedDialog = {
  readonly cancel: () => void
  readonly dialogRef: RefObject<HTMLDialogElement | null>
  readonly onDialogClose: () => void
}

export const useManagedDialog = (open: boolean, onClose: () => void): ManagedDialog => {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const wasOpenRef = useRef(false)

  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog === null) return
    if (open && !wasOpenRef.current) {
      const activeElement = document.activeElement
      returnFocusRef.current = activeElement instanceof HTMLElement && !dialog.contains(activeElement) ? activeElement : null
    }
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
    wasOpenRef.current = open
    return () => {
      // Activity retains hidden tab DOM. Release modal inertness when its effects pause.
      if (dialog.open) dialog.close()
    }
  }, [open])

  const cancel = useCallback(() => {
    dialogRef.current?.close()
  }, [])

  const onDialogClose = useCallback(() => {
    onClose()
    const returnFocus = returnFocusRef.current
    returnFocusRef.current = null
    window.requestAnimationFrame(() => returnFocus?.focus())
  }, [onClose])

  return { cancel, dialogRef, onDialogClose }
}
